/**
 * BackupPanel — sdílený panel pro backup/snapshot/restore instance.
 *
 * Použití (v supabase / apphost modulu):
 *   <BackupPanel
 *     module="supabase"               // nebo "apphost"
 *     instanceName={inst.name}         // prefix pro filtering (backup label začíná na {name}-)
 *     containerPrefix={inst.name}      // pattern pro backupGroup/snapshotGroup (prvek před '-')
 *   />
 *
 * Backend:
 *   - POST /mcp tool="backup_group_create" { module: containerPrefix, label: ... }
 *   - POST /mcp tool="snapshot_group_create" { module: containerPrefix }
 *   - POST /mcp tool="backup_list" { }  (filter client-side podle label)
 *   - POST /mcp tool="snapshot_list" { }
 *   - POST /mcp tool="snapshot_revert" { snapshot, container }
 *   - POST /mcp tool="backup_restore" { name } (volá backup_restore tool)
 *
 * UX:
 *   - Sekce "Snapshot" = rychlý lokální snapshot (docker commit)
 *   - Sekce "Backup"   = plná přenosná záloha (docker save + volumes tar)
 *   - Každá akce má confirm toast + progress loader
 */

import { useState } from "react";
import { mcpCall } from "../../lib/mcp";
import { downloadBase64 } from "../../lib/download";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Archive,
  Camera,
  Download,
  Loader2,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Button } from "./Button";
import { ConfirmDialog } from "./ConfirmDialog";
// api import nepotřebujeme — MCP volání přes fetch('/mcp'). Ponecháno pro budoucí rozšíření.

interface Props {
  /** Nadřazený ACM modul, slouží pro jméno zálohy a pro audit (ne filtrování). */
  module: string;
  /** Jméno instance — pro label a filter. */
  instanceName: string;
  /**
   * Prefix kontejnerů — tohle backup_group_create vezme jako `module` parametr
   * (viz backup-utils: filtruje kontejnery co začínají na `{containerPrefix}-`).
   * Pro supabase = {name} (kontejnery {name}-supabase-*).
   * Pro apphost  = `apphost-{name}` (kontejnery apphost-{name}-*).
   */
  containerPrefix: string;
}

interface SnapshotMeta {
  name: string;
  container: string;
  image: string;
  created: string;
  size?: string;
}
interface BackupMeta {
  name: string;
  container: string;
  image: string;
  created: string;
  size?: string;
}

async function callMcp<T = unknown>(
  tool: string,
  args: Record<string, unknown>,
): Promise<T> {
  return await mcpCall<T>(tool, args);
}

export function BackupPanel({ module, instanceName, containerPrefix }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<
    | { kind: "deleteSnapshot"; name: string }
    | { kind: "deleteBackup"; name: string }
    | { kind: "restoreSnapshot"; name: string; container: string }
    | null
  >(null);

  // Filter label prefix — všechny backupy co vznikly pro tuhle instanci mají
  // buď `pre-delete-{name}-...` (auto před delete) nebo `{containerPrefix}-group-...`
  // (manuální). Filtrujeme na klientovi — backup_list nepřijímá filter parametr.
  const namePrefixes = [
    `pre-delete-${instanceName}-`,
    `${containerPrefix}-group-`,
    // Jednotlivé kontejnery instance (když někdo zálohuje per-container)
    `${containerPrefix}-`,
  ];

  const matchesInstance = (n: string) =>
    namePrefixes.some((p) => n.startsWith(p));

  const snapshots = useQuery({
    queryKey: ["snapshots", containerPrefix],
    queryFn: async () => {
      const r = await callMcp<{ ok: boolean; snapshots: SnapshotMeta[] }>(
        "snapshot_list",
        {},
      );
      const list = Array.isArray(r.snapshots) ? r.snapshots : [];
      return list.filter((s) => matchesInstance(s.name));
    },
    refetchInterval: busy ? 2000 : false,
  });

  const backups = useQuery({
    queryKey: ["backups", containerPrefix],
    queryFn: async () => {
      const r = await callMcp<{ ok: boolean; backups: BackupMeta[] }>(
        "backup_list",
        {},
      );
      const list = Array.isArray(r.backups) ? r.backups : [];
      return list.filter((b) => matchesInstance(b.name));
    },
    refetchInterval: busy ? 2000 : false,
  });

  const createSnapshot = useMutation({
    mutationFn: async () => {
      setBusy("snapshot");
      return callMcp("snapshot_group_create", { module: containerPrefix });
    },
    onSuccess: (r: unknown) => {
      const typed = r as { ok?: number; failed?: number };
      toast.success(
        `Snapshot: ${typed.ok ?? "?"} OK / ${typed.failed ?? 0} failed`,
      );
      qc.invalidateQueries({ queryKey: ["snapshots", containerPrefix] });
      setBusy(null);
    },
    onError: (e: Error) => {
      toast.error(t('backup.snapshotFailed', 'Snapshot selhal: {{message}}', { message: e.message }));
      setBusy(null);
    },
  });

  const createBackup = useMutation({
    mutationFn: async () => {
      setBusy("backup");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      return callMcp("backup_group_create", {
        module: containerPrefix,
        label: `${containerPrefix}-manual-${ts}`,
      });
    },
    onSuccess: (r: unknown) => {
      const typed = r as { ok?: number; failed?: number };
      toast.success(
        `Backup: ${typed.ok ?? "?"} OK / ${typed.failed ?? 0} failed`,
      );
      qc.invalidateQueries({ queryKey: ["backups", containerPrefix] });
      setBusy(null);
    },
    onError: (e: Error) => {
      toast.error(t('backup.backupFailed', 'Backup selhal: {{message}}', { message: e.message }));
      setBusy(null);
    },
  });

  const revertSnapshot = useMutation({
    mutationFn: async (
      { snapshot, container }: { snapshot: string; container: string },
    ) => {
      setBusy("revert");
      // Pre-revert snapshot — zachytí aktuální stav před overwrite.
      // Pokud selže (např. kontejner offline), revert pokračuje ale uvidíme to v toastu.
      let preSnap: string | null = null;
      try {
        const pre = await callMcp<
          { ok: boolean; snapshot?: { name?: string } }
        >(
          "snapshot_create",
          { container },
        );
        preSnap = pre.snapshot?.name ?? null;
      } catch (e) {
        toast.warning(
          t(
            'backup.preRevertFailed',
            'Pre-revert snapshot selhal ({{message}}) — revert pokračuje',
            { message: (e as Error).message },
          ),
        );
      }
      const r = await callMcp("snapshot_revert", { snapshot, container });
      return { revert: r, preSnap };
    },
    onSuccess: (r: { preSnap: string | null }) => {
      toast.success(
        r.preSnap
          ? t('backup.snapshotRestoredWithPre', 'Snapshot obnoven. Pre-revert snapshot: {{name}}', {
            name: r.preSnap,
          })
          : t('backup.snapshotRestored', 'Snapshot obnoven (bez pre-revert snapshotu)'),
      );
      qc.invalidateQueries({ queryKey: ["snapshots", containerPrefix] });
      setBusy(null);
    },
    onError: (e: Error) => {
      toast.error(t('backup.revertFailed', 'Revert selhal: {{message}}', { message: e.message }));
      setBusy(null);
    },
  });

  const deleteSnapshot = useMutation({
    mutationFn: async (name: string) => {
      setBusy("delete-snap");
      return callMcp("snapshot_delete", { snapshot: name });
    },
    onSuccess: () => {
      toast.success(t('ui.snapshotDeleted', 'Snapshot smazán'));
      qc.invalidateQueries({ queryKey: ["snapshots", containerPrefix] });
      setBusy(null);
    },
    onError: (e: Error) => {
      toast.error(t('backup.deleteFailed', 'Delete selhal: {{message}}', { message: e.message }));
      setBusy(null);
    },
  });

  const downloadBackup = useMutation({
    mutationFn: async (name: string) => {
      return callMcp<
        {
          ok: boolean;
          filename?: string;
          content_type?: string;
          base64?: string;
          error?: string;
          too_large?: boolean;
          node_path?: string;
        }
      >("toolbox.backup_download", { file: name });
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(
          r.too_large && r.node_path
            ? t(
              'backup.tooLarge',
              'Záloha je moc velká pro stažení přes dashboard. Najdeš ji na uzlu: {{path}}',
              { path: r.node_path },
            )
            : r.error ?? t('ui.downloadFailed', 'Stažení selhalo'),
        );
        return;
      }
      if (r.base64 && r.filename) downloadBase64(r.base64, r.filename, r.content_type);
    },
    onError: (e: Error) =>
      toast.error(t('backup.downloadFailedNamed', 'Stažení selhalo: {{message}}', { message: e.message })),
  });

  const deleteBackup = useMutation({
    mutationFn: async (name: string) => {
      setBusy("delete-bkp");
      return callMcp("backup_delete", { backup: name });
    },
    onSuccess: () => {
      toast.success(t('ui.backupDeleted', 'Záloha smazána'));
      qc.invalidateQueries({ queryKey: ["backups", containerPrefix] });
      setBusy(null);
    },
    onError: (e: Error) => {
      toast.error(t('backup.deleteFailed', 'Delete selhal: {{message}}', { message: e.message }));
      setBusy(null);
    },
  });

  return (
    <div className="space-y-4">
      {/* Akce */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => createSnapshot.mutate()}
          disabled={busy !== null}
        >
          {busy === "snapshot"
            ? <Loader2 size={14} className="animate-spin" />
            : <Camera size={14} />}
          Snapshot
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => createBackup.mutate()}
          disabled={busy !== null}
        >
          {busy === "backup"
            ? <Loader2 size={14} className="animate-spin" />
            : <Archive size={14} />}
          {t('backup.backupNow', 'Zálohovat teď')}
        </Button>
        <span className="text-[10px] text-gray-500 self-center ml-2">
          {t('backup.moduleLabel', 'Modul:')}{' '}
          <span className="font-mono text-gray-400">{module}</span>
        </span>
      </div>

      {/* Snapshots */}
      <section>
        <div className="flex items-center justify-between mb-1.5">
          <h4 className="text-xs font-medium text-gray-400">
            {t('backup.snapshots', 'Snapshoty')}{' '}
            {snapshots.data && `(${snapshots.data.length})`}
          </h4>
          <span className="text-[10px] text-gray-500">
            {t('backup.snapshotHint', 'rychlé, lokální (docker commit)')}
          </span>
        </div>
        <ItemList
          items={snapshots.data ?? []}
          loading={snapshots.isLoading}
          emptyText={t("ui.noSnapshots", "Žádné snapshoty.")}
          renderAction={(item) => (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() =>
                  setConfirm({
                    kind: "restoreSnapshot",
                    name: item.name,
                    container: item.container,
                  })}
                disabled={busy !== null}
                className="text-[10px] text-blue-400 hover:text-blue-300 disabled:opacity-50 flex items-center gap-1"
                title={t("ui.restore", "Obnovit")}
              >
                <RotateCcw size={10} /> {t("ui.restore", "Obnovit")}
              </button>
              <button
                type="button"
                onClick={() => setConfirm({ kind: "deleteSnapshot", name: item.name })}
                disabled={busy !== null}
                className="text-[10px] text-gray-500 hover:text-red-400 disabled:opacity-50 flex items-center gap-1"
                title={t("ui.delete", "Smazat")}
              >
                <Trash2 size={10} />
              </button>
            </div>
          )}
        />
      </section>

      {/* Backups */}
      <section>
        <div className="flex items-center justify-between mb-1.5">
          <h4 className="text-xs font-medium text-gray-400">
            {t('backup.backups', 'Zálohy')} {backups.data && `(${backups.data.length})`}
          </h4>
          <span className="text-[10px] text-gray-500">
            {t('backup.backupHint', 'přenosné, tar.gz (cross-server)')}
          </span>
        </div>
        <ItemList
          items={backups.data ?? []}
          loading={backups.isLoading}
          emptyText={t("ui.noBackups", "Žádné zálohy.")}
          renderAction={(item) => (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadBackup.mutate(item.name);
                }}
                disabled={downloadBackup.isPending}
                className="text-[10px] text-blue-400 hover:text-blue-300 disabled:opacity-50 flex items-center gap-1"
                title={t("ui.download", "Stáhnout")}
              >
                {downloadBackup.isPending && downloadBackup.variables === item.name
                  ? <Loader2 size={10} className="animate-spin" />
                  : <Download size={10} />}
              </button>
              <button
                type="button"
                onClick={() => setConfirm({ kind: "deleteBackup", name: item.name })}
                disabled={busy !== null}
                className="text-[10px] text-gray-500 hover:text-red-400 disabled:opacity-50 flex items-center gap-1"
                title={t("ui.delete", "Smazat")}
              >
                <Trash2 size={10} />
              </button>
            </div>
          )}
        />
      </section>

      <ConfirmDialog
        open={!!confirm}
        title={
          confirm?.kind === "restoreSnapshot"
            ? t("ui.restore", "Obnovit")
            : t("ui.deleteItemTitle", "Smazat položku?")
        }
        description={
          confirm?.kind === "restoreSnapshot"
            ? t("ui.restoreSnapshotConfirm", "Obnovit snapshot {{name}}? Kontejner bude nahrazen; před obnovou se vytvoří pre-revert snapshot.", { name: confirm.name })
            : confirm?.kind === "deleteSnapshot"
            ? t("ui.deleteSnapshotConfirm", "Smazat snapshot {{name}}?", { name: confirm.name })
            : confirm?.kind === "deleteBackup"
            ? t("ui.deleteBackupConfirm", "Smazat zálohu {{name}}?", { name: confirm.name })
            : undefined
        }
        confirmLabel={
          confirm?.kind === "restoreSnapshot"
            ? t("ui.restore", "Obnovit")
            : t("ui.delete", "Smazat")
        }
        cancelLabel={t("ui.cancel", "Zrušit")}
        danger={confirm?.kind !== "restoreSnapshot"}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === "restoreSnapshot") {
            revertSnapshot.mutate({
              snapshot: confirm.name,
              container: confirm.container,
            });
          } else if (confirm.kind === "deleteSnapshot") {
            deleteSnapshot.mutate(confirm.name);
          } else if (confirm.kind === "deleteBackup") {
            deleteBackup.mutate(confirm.name);
          }
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function ItemList<
  T extends { name: string; container: string; created: string; size?: string },
>(
  { items, loading, emptyText, renderAction }: {
    items: T[];
    loading: boolean;
    emptyText: string;
    renderAction: (item: T) => React.ReactNode;
  },
) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> {t("common.loading", "Načítám…")}
      </div>
    );
  }
  if (items.length === 0) {
    return <div className="text-xs text-gray-500 italic">{emptyText}</div>;
  }
  return (
    <div className="border border-gray-800 rounded-lg divide-y divide-gray-800">
      {items.map((item) => (
        <div
          key={item.name}
          className="flex items-center gap-3 px-3 py-2 text-xs"
        >
          <div className="flex-1 min-w-0">
            <div className="font-mono text-gray-200 truncate">{item.name}</div>
            <div className="text-[10px] text-gray-500">
              <span className="text-gray-400">{item.container}</span>
              {" · "}
              {new Date(item.created).toLocaleString("cs-CZ", {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {item.size && <>· {item.size}</>}
            </div>
          </div>
          {renderAction(item)}
        </div>
      ))}
    </div>
  );
}
